import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Toast, type ToastType } from "@legal-500/legal500-design-system";
import { usePermissions } from "@/hooks/usePermissions";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  researchCms,
  fetchAllEditionBpas,
  fetchAllEditions,
  type EditorialContentDetail,
  type EditorialCommentary,
  type TranslationTarget,
  type TranslationsView,
} from "@/api/researchCms.service";
import { companies } from "@/api/addressBook.service";
import { docParsing } from "@/api/docParsing.service";
import { CmsEmptyState } from "@/components/ResearchCms";
import { FormProgressBar } from "@/components/ResearchCms/FormProgressBar/FormProgressBar";
import { UnsavedChangesModal } from "@/components/ResearchCms/UnsavedChangesModal/UnsavedChangesModal";
import { MultipleFirmTagsModal } from "@/components/ResearchCms/MultipleFirmTagsModal/MultipleFirmTagsModal";
import { UnscoredQuestionsModal } from "@/components/ResearchCms/UnscoredQuestionsModal/UnscoredQuestionsModal";
import { PageHeader } from "../../../components/PageHeader/PageHeader";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import { ROUTES } from "../../../routes";
import { CmsLayout } from "./components/CmsLayout";
import { AutoTranslatedNotice } from "./components/AutoTranslatedNotice";
import { SourceDataPanel } from "./components/SourceDataPanel/SourceDataPanel";
import type { SourceDataSource } from "./components/SourceDataPanel/SourceDataPanel";
import { OrgBanner } from "./components/OrgBanner/OrgBanner";
import { TemplateSubmissionTab } from "./components/SubmissionTab/TemplateSubmissionTab";
import { EditorialTab } from "./components/EditorialTab/EditorialTab";
import { DetailsTab } from "./components/DetailsTab/DetailsTab";
import { RankingsTab } from "./components/RankingsTab/RankingsTab";
import { GATE_MESSAGE, TranslationsTab } from "./components/TranslationsTab/TranslationsTab";
import { orgFormResolver, type OrgFormValues } from "./schemas/orgViewSchemas";
import {
  EMPTY_ORG_FORM,
  mergeAdoptedValues,
  toOrgFormValues,
  toUpdateRequest,
  toCommentaryFormValues,
  toCommentaryRequest,
  commentaryLanguage,
  hasCommentaryContent,
  hasSubmissionContent,
  type RecordedSarsScores,
} from "./utils/orgDetailMapper";
import {
  selectSarsSliders,
  planSarsWrite,
  mergeSarScores,
  type SarsSlider,
  type SarsWritePlan,
} from "./utils/sarsFields";
import { findOrgContentId } from "./utils/findOrgContentId";
import { toTranslationsFormValues, translationsSaveBody } from "./utils/translationsForm";
import { findFirmTagsToConfirm } from "./utils/taggedFirms";
import { firmSubmissionTable, orderedFirmTables } from "./utils/firmSubmissionTable";
import { rankingTableStamp } from "./utils/rankingTableStamp";
import {
  findUnsavableRankingEdits,
  UNSAVABLE_RANKING_MESSAGE,
} from "./utils/unsavableRankingEdits";
import { STATUS_CHANGE_MESSAGES } from "./utils/statusChangeMessages";
import { canSetReviewStatus } from "./utils/reviewStatusPermission";
import { filterOrgFields } from "./utils/filterFieldsByContext";
import { resolveEffectiveTemplateType } from "./utils/resolveTemplateType";
import { withSaveTimeout } from "./saveRequestTimeout";
import { useNavigationGuard } from "./hooks/useNavigationGuard";
import type { CmsNavState, EditorialReviewStatus, EditorialWriteStatus } from "./types";
import shared from "./components/cmsShared.module.css";
import styles from "./CmsOrgPage.module.css";

const EMPTY_SLIDERS: SarsSlider[] = [];

const TABS = [
  { id: "rankings", label: "Rankings" },
  { id: "submission", label: "Submission" },
  { id: "editorial", label: "Editorial" },
  { id: "translations", label: "Translations" },
  { id: "details", label: "Details" },
] as const;

const TRANSLATION_GATE_STATUSES: ReadonlyArray<EditorialReviewStatus> = [
  "ready_for_translation",
  "ready_for_release",
];

const MANUAL_ENTRY_PERMISSION_MESSAGE =
  "Creating a manual record requires the research create and edit permissions.";
const DUPLICATE_RECORD_MESSAGE = "This firm already has a record for this practice area.";
const NO_EDITION_BPA_MESSAGE =
  "This practice area is not linked to the edition, so commentary cannot be saved.";

function isConflict(err: unknown): boolean {
  return err instanceof Error && (err as Error & { status?: number }).status === 409;
}

/** The conflict names the existing row inline: "... (id: <uuid>, reviewStatus: pending)". */
function conflictContentId(err: unknown): string | null {
  const match = err instanceof Error ? err.message.match(/id: ([0-9a-f-]{36})/i) : null;
  return match ? match[1] : null;
}

function saveErrorText(err: unknown): string {
  if (isConflict(err)) return DUPLICATE_RECORD_MESSAGE;
  return err instanceof Error ? err.message : "Failed to save";
}

async function createOrAdoptOrgContent(input: {
  companyId: string;
  researchBpaId: string;
  editionId: string;
  directoryId: string;
  firmName: string;
  rankingTableId?: string;
  officeId?: string;
  officeName?: string;
}): Promise<EditorialContentDetail> {
  try {
    const created = await withSaveTimeout(() => researchCms.createEditorialContent(input));
    return created.editorialContent;
  } catch (err) {
    if (!isConflict(err)) throw err;
    // Another researcher created the row first; adopt it so the typed values still land.
    const existingId =
      conflictContentId(err) ??
      (await findOrgContentId({
        editionId: input.editionId,
        bpaId: input.researchBpaId,
        companyUuid: input.companyId,
        rankingTableId: input.rankingTableId,
      }));
    if (!existingId) throw err;
    return (await researchCms.getEditorialContent(existingId)).editorialContent;
  }
}

const PUBLICATIONS_QUERY = {
  queryKey: ["research-publications-list"],
  queryFn: async () => (await researchCms.getPublications()).publications,
};

export function CmsOrgPage() {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const { hasFlag } = useFeatureFlags();
  const {
    editionId: routeEditionId,
    bpaId: routeBpaId,
    companyUuid: routeCompanyUuid,
  } = useParams<{
    editionId: string;
    bpaId: string;
    companyUuid: string;
  }>();
  const location = useLocation();
  const navState = location.state as CmsNavState;

  const companyName = navState?.companyName?.trim() ?? "";

  const editionId = routeEditionId!;
  const bpaId = routeBpaId!;
  const companyUuid = routeCompanyUuid!;

  const { data: addressBookCompany } = useQuery({
    queryKey: ["address-book-company", companyUuid],
    queryFn: () => companies.getById(companyUuid),
  });

  const hasNavState = Boolean(navState?.bpaName);

  const { data: apiData, isLoading: apiLoading } = useQuery({
    queryKey: ["research-org-api-data", editionId, bpaId],
    queryFn: async () => {
      const [allBpas, allEditions] = await Promise.all([
        fetchAllEditionBpas({ editionId }),
        fetchAllEditions(),
      ]);

      const bpa = allBpas.find(b => b.bpaId === bpaId);
      const edition = allEditions.find(e => e.editionId === editionId);

      const prevConfigId = edition?.previousEditionId ?? null;
      const prevConfig = prevConfigId ? allEditions.find(e => e.id === prevConfigId) : null;

      return {
        bpaName: bpa?.bpaName ?? null,
        rankingEntityId: bpa?.rankingEntityId ?? null,
        rankingEntityName: bpa?.rankingEntityName ?? null,
        rankingEntityType: bpa?.rankingEntityType ?? null,
        editionName: edition?.editionName ?? null,
        previousEditionId: prevConfig?.editionId ?? null,
      };
    },
  });

  const bpaName = navState?.bpaName ?? apiData?.bpaName ?? "";
  const rankingEntityId = navState?.rankingEntityId ?? apiData?.rankingEntityId ?? "";
  const rankingEntityName = navState?.rankingEntityName ?? apiData?.rankingEntityName ?? "";
  const rankingEntityType = navState?.rankingEntityType ?? apiData?.rankingEntityType ?? null;
  const editionName = navState?.editionName ?? apiData?.editionName ?? "";

  const [searchParams, setSearchParams] = useSearchParams();

  const { data: rankingTablesData, isError: rankingTablesFailed } = useQuery({
    queryKey: ["research-ranking-tables", bpaId, editionId],
    queryFn: () => researchCms.getRankingTables({ bpaId, editionId }),
  });

  const { data: allContentRows, isPending: contentRowsPending } = useQuery({
    queryKey: ["research-org-all-content", editionId, bpaId, companyUuid],
    queryFn: async () => {
      const result = await researchCms.getEditorialContentList({
        editionId,
        bpaId,
        companyId: companyUuid,
        level: "organisation",
        limit: 50,
      });
      return result.editorialContent;
    },
  });

  const contentTableIds = useMemo(
    () =>
      new Set(
        (allContentRows ?? []).flatMap(row => (row.rankingTableId ? [row.rankingTableId] : [])),
      ),
    [allContentRows],
  );

  const orgSubmissionTables = useMemo(
    () => orderedFirmTables(rankingTablesData?.rankingTables, contentTableIds),
    [rankingTablesData, contentTableIds],
  );

  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [manualTabsOpened, setManualTabsOpened] = useState<Set<string>>(new Set());

  // Reset the sub-tab selection when the BPA changes — the tables differ per BPA.
  useEffect(() => {
    setSelectedTableId(null);
  }, [bpaId, editionId]);

  const firmTable = firmSubmissionTable({
    rankingTables: rankingTablesData?.rankingTables,
    contentTableIds,
    selectedTableId,
  });
  const tableResolved = firmTable.ready && !contentRowsPending;
  const rankingTableId = tableResolved ? firmTable.rankingTableId : undefined;
  const activeTableId = rankingTableId ?? null;

  const firmTables = rankingTablesData?.rankingTables.filter(t => t.isLawyerTable === 0) ?? [];
  const hasFirmSubmission =
    rankingTablesData === undefined || firmTables.some(t => t.submissionType === "firm");

  const {
    data: firmRankingsMap,
    isFetching: isFetchingRankings,
    refetch: refetchRankings,
  } = useQuery({
    queryKey: ["research-firm-rankings-form", bpaId, editionId, companyUuid],
    queryFn: async () => {
      const results = await Promise.all(
        firmTables.map(async table => {
          const data = await researchCms.getFirmRankings(table.id, {
            companyId: companyUuid,
          });
          const ranking = "firmRankings" in data ? (data.firmRankings[0] ?? null) : null;
          return [table.id, ranking] as const;
        }),
      );
      const map: OrgFormValues["rankings"] = {};
      for (const [tableId, ranking] of results) {
        map[tableId] = {
          rankingId: ranking?.id ?? null,
          tier: ranking?.rankingTier ?? null,
          status: (ranking?.status as "draft" | "pending" | "published" | "archived") ?? "draft",
          ranked: ranking !== null,
          comment: ranking?.rankingComment ?? "",
          displayOrder: ranking?.displayOrder ?? 0,
        };
      }
      return map;
    },
    enabled: firmTables.length > 0,
  });
  const prevEditionId = apiData?.previousEditionId ?? null;

  const { data: previousTiersData } = useQuery({
    queryKey: ["research-prev-firm-rankings", bpaId, prevEditionId, companyUuid],
    queryFn: async () => {
      const prevTables = await researchCms.getRankingTables({
        bpaId,
        editionId: prevEditionId!,
      });
      const prevFirmTables = prevTables.rankingTables.filter(t => t.isLawyerTable === 0);

      const results = await Promise.all(
        prevFirmTables.map(async table => {
          const data = await researchCms.getFirmRankings(table.id, {
            companyId: companyUuid,
          });
          const ranking = "firmRankings" in data ? (data.firmRankings[0] ?? null) : null;
          return {
            name: table.name,
            subtype: table.subtype,
            tier: ranking?.rankingTier ?? null,
          };
        }),
      );

      const map: Record<string, number | null> = {};
      for (const prev of results) {
        const currentTable = firmTables.find(
          t => t.name === prev.name && t.subtype === prev.subtype,
        );
        if (currentTable) map[currentTable.id] = prev.tier;
      }
      return map;
    },
    enabled: firmTables.length > 0 && prevEditionId !== null,
    staleTime: 300_000,
  });

  const previousTiers = previousTiersData ?? {};

  const firmRankingsMetadata = useMemo(
    () =>
      firmTables.map(table => ({
        rankingTableId: table.id,
        rankingTableName: table.name,
        rankingId: firmRankingsMap?.[table.id]?.rankingId ?? null,
      })),
    [firmTables, firmRankingsMap],
  );

  const selectedTableName = orgSubmissionTables.find(t => t.id === activeTableId)?.name ?? null;

  const { data: scopedRankingData } = useQuery({
    queryKey: ["research-scoped-firm-ranking", rankingTableId, companyUuid],
    queryFn: () => researchCms.getFirmRankings(rankingTableId!, { companyId: companyUuid }),
    enabled: rankingTableId !== undefined,
  });
  const scopedRankingId =
    scopedRankingData && "firmRankings" in scopedRankingData
      ? (scopedRankingData.firmRankings[0]?.id ?? null)
      : null;

  const defaultTab = "rankings";
  const activeTab = searchParams.get("tab") ?? defaultTab;
  const setActiveTab = useCallback(
    (tab: string) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set("tab", tab);
          return next;
        },
        { state: navState },
      );
    },
    [setSearchParams, navState],
  );

  const {
    data,
    error,
    isFetching: isFetchingDetail,
    refetch,
  } = useQuery({
    queryKey: ["research-org-detail", editionId, bpaId, companyUuid, rankingTableId],
    queryFn: async () => {
      const [rowId, editions, editionBpas] = await Promise.all([
        findOrgContentId({
          editionId,
          bpaId,
          companyUuid,
          rankingTableId,
        }),
        researchCms.getEditions({ editionId, limit: 1 }),
        researchCms.getEditionBpas({ editionId, bpaId, limit: 1 }),
      ]);
      const editionConfig = editions.editionConfigs[0];
      const directoryId = editionConfig?.directoryId ?? null;
      const templateType = editionConfig?.templateType ?? null;
      const editionStatus = editionConfig?.status ?? null;
      const editionBpaId = editionBpas.editionBpas[0]?.id ?? null;
      const detail = rowId ? (await researchCms.getEditorialContent(rowId)).editorialContent : null;
      const publications = directoryId
        ? await queryClient.ensureQueryData(PUBLICATIONS_QUERY)
        : undefined;
      const languageIso = commentaryLanguage(
        detail,
        publications?.find(pub => pub.directoryId === directoryId),
      );
      const commentary = editionBpaId
        ? await researchCms.getEditorialCommentary(editionBpaId, {
            companyId: companyUuid,
            languageIso,
          })
        : null;
      return {
        detail,
        directoryId,
        templateType,
        editionStatus,
        editionBpaId,
        commentary,
      };
    },
  });

  const detail = data?.detail ?? null;
  const editionBpaId = data?.editionBpaId ?? null;
  const directoryId = data?.directoryId ?? null;
  const templateType = data?.templateType ?? null;
  // Bar practice areas within a conventional publication need bar template fields.
  const effectiveTemplateType = resolveEffectiveTemplateType(rankingEntityType, templateType);
  const editionStatus = data?.editionStatus ?? null;
  const loadedCommentary = data?.commentary ?? null;
  const isManualEntry = data !== undefined && data.detail === null;
  const isDeleted = detail?.reviewStatus === "deleted";
  const hasLoaded = data !== undefined;

  const { data: fileUrlData } = useQuery({
    queryKey: ["submission-file-url", detail?.docParseResultId],
    queryFn: () => docParsing.getFileUrl(detail!.docParseResultId!),
    enabled: detail?.docParseResultId != null,
  });

  // Keyed to match CmsPublicationPage; ResearchCmsHome's key holds a different shape
  // so sharing it would serve the wrong data after navigating from the home page.
  const { data: publications } = useQuery(PUBLICATIONS_QUERY);
  const publication = publications?.find(pub => pub.directoryId === directoryId);
  // The translation step only applies when a publication is written in one language and published
  // in another; an unresolved publication reads as no translation.
  const needsTranslation =
    publication !== undefined && publication.inputLanguage !== publication.outputLanguage;
  const translationsEnabled = needsTranslation && hasFlag("research-cms-1-enabled");

  // The edition config already carries the template type, so this only fetches the field
  // list once that is known — never for a manual-entry row whose edition has no directory.
  // When the ranking entity is a bar, the effective type overrides the publication's own
  // template so the page fetches bar-specific fields instead.
  // One query feeds the SARs sliders, the submission field list and the clerks gate.
  const { data: templateFieldsData } = useQuery({
    queryKey: ["research-template-fields", effectiveTemplateType],
    queryFn: async () => {
      if (!effectiveTemplateType) return null;
      const config = await researchCms.getTemplateFields(effectiveTemplateType);
      return config.templateFields;
    },
    enabled: effectiveTemplateType !== null,
  });

  const templateFields = Array.isArray(templateFieldsData) ? templateFieldsData : [];

  const sarsSliders = useMemo(
    () =>
      templateFields.length > 0 ? selectSarsSliders(templateFields, "organisation") : EMPTY_SLIDERS,
    [templateFields],
  );

  const submissionFields = useMemo(
    () =>
      filterOrgFields(templateFields).filter(f => f.fieldTab === "submission" && f.editable === 1),
    [templateFields],
  );

  const showClerksCommentary = useMemo(
    () => templateFields.some(f => f.fieldName === "bar_editorial_clerks"),
    [templateFields],
  );

  const contentId = detail?.id ?? null;
  // `{ sars: null }` with 200 means nobody has scored yet (not a failure). A failed read
  // must be distinguishable so the next save doesn't overwrite real stored scores.
  const {
    data: sarsData,
    error: sarsError,
    isFetching: isFetchingSars,
    refetch: refetchSars,
  } = useQuery({
    queryKey: ["research-sars", contentId],
    queryFn: async () => {
      if (!contentId) return null;
      return researchCms.getSars(contentId);
    },
    enabled: contentId !== null,
  });
  const recordedSarsScores: RecordedSarsScores | null = sarsData?.sars ?? null;

  // Scores live in their own query; refetching content alone re-hydrates from a stale score set.
  // Both must be refetched together so the hydration effect sees current data.
  const refetchContentAndScores = useCallback(async () => {
    await Promise.all([refetch(), refetchSars()]);
  }, [refetch, refetchSars]);

  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [createdContent, setCreatedContent] = useState<EditorialContentDetail | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
  } | null>(null);
  const [unscoredLabels, setUnscoredLabels] = useState<string[] | null>(null);
  const [taggedFirmNames, setTaggedFirmNames] = useState<string[] | null>(null);
  const [pendingValues, setPendingValues] = useState<OrgFormValues | null>(null);
  const isBusy = isSaving || isTransitioning;

  const [savedCommentary, setSavedCommentary] = useState<EditorialCommentary | null>(null);
  const commentary = savedCommentary ?? loadedCommentary;

  // Param-only navigation keeps the component mounted; per-row state must not
  // leak from the previous row into a save against the new one.
  useEffect(() => {
    setSavedCommentary(null);
    setCreatedContent(null);
    setManualTabsOpened(new Set());
  }, [editionId, bpaId, companyUuid]);

  const form = useForm<OrgFormValues>({
    resolver: orgFormResolver,
    defaultValues: EMPTY_ORG_FORM,
  });

  const draftsByTable = useRef(new Map<string, OrgFormValues>());

  useEffect(() => {
    if (!detail && !isManualEntry) return;
    const stashed = rankingTableId ? draftsByTable.current.get(rankingTableId) : undefined;
    if (stashed) {
      form.reset(stashed);
      setIsHydrated(true);
      return;
    }
    // A refetch lands query by query; hydrating from a half-refreshed snapshot shows stale data.
    if (isFetchingDetail || isFetchingSars || isFetchingRankings) return;

    const values = detail
      ? toOrgFormValues(detail, loadedCommentary, recordedSarsScores)
      : { ...EMPTY_ORG_FORM, ...toCommentaryFormValues(loadedCommentary) };
    for (const slider of sarsSliders) {
      if (!(slider.scoreKey in values.sarScores)) {
        values.sarScores[slider.scoreKey] = null;
      }
    }
    const cachedTranslations = detail
      ? queryClient.getQueryData<TranslationsView>(["research-translations", detail.id])
      : undefined;
    form.reset({
      ...values,
      rankings: firmRankingsMap ?? EMPTY_ORG_FORM.rankings,
      translations: toTranslationsFormValues(cachedTranslations ?? null),
    });
    setIsHydrated(true);
  }, [
    detail,
    loadedCommentary,
    isManualEntry,
    recordedSarsScores,
    sarsSliders,
    firmRankingsMap,
    rankingTableId,
    isFetchingDetail,
    isFetchingSars,
    isFetchingRankings,
    form,
    queryClient,
  ]);

  const { isDirty } = form.formState;
  const navGuard = useNavigationGuard(isDirty);

  const watched = form.watch();
  // Editorial-tab fields are off screen while the flag is off, so they leave the denominator too.
  const rankingEntries = Object.entries(watched.rankings ?? {});
  const rankingFieldCount = rankingEntries.length;
  const filledRankingCount = rankingEntries.filter(
    ([, r]) => r.tier !== null || r.ranked || (r.comment ?? "").trim() !== "",
  ).length;

  const aggregateContent = allContentRows ?? [];
  const aggregatePopulated = aggregateContent.reduce((n, row) => n + (row.populatedFields ?? 0), 0);
  const aggregateTotal = aggregateContent.reduce((n, row) => n + (row.totalFields ?? 0), 0);
  const templateFieldTotal = submissionFields.length + sarsSliders.length;
  const manualTabsExtra = manualTabsOpened.size * templateFieldTotal;

  const filledFieldCount = aggregatePopulated + filledRankingCount;
  const totalFields = aggregateTotal + manualTabsExtra + rankingFieldCount;

  const canEdit = hasPermission("staff.research.edit");
  const canApprove = hasPermission("staff.research.approve");
  const canCreate = hasPermission("staff.research.create");
  // Manual entry saves as POST-then-PATCH, so it needs both grants: with create alone the
  // POST lands and the PATCH 403s, committing an empty row.
  const canCreateManualEntry = canCreate && canEdit;
  // Mirrors the backend's per-target status gate so a transition the actor lacks the
  // permission for is disabled up front rather than clicked into a 403.
  const canSetStatus = useCallback(
    (status: EditorialWriteStatus) => canSetReviewStatus(hasPermission, status),
    [hasPermission],
  );

  const translationsGateOpen =
    detail !== null && TRANSLATION_GATE_STATUSES.includes(detail.reviewStatus);
  const translationsQuery = useQuery({
    queryKey: ["research-translations", detail?.id],
    queryFn: () => researchCms.getTranslations(detail!.id),
    enabled: translationsEnabled && translationsGateOpen && activeTab === "translations",
  });
  const translationsView = translationsQuery.data ?? null;
  const [translationBusy, setTranslationBusy] = useState<string | null>(null);
  const [translationError, setTranslationError] = useState<string | null>(null);
  const translationsDirty = Boolean(form.formState.dirtyFields.translations);

  // resetField only reaches fields the tab has registered, so this runs once the loaded view
  // has rendered its inputs; a full reset from the hydration effect covers the earlier window.
  useEffect(() => {
    if (!translationsQuery.data) return;
    form.resetField("translations", {
      defaultValue: toTranslationsFormValues(translationsQuery.data),
    });
  }, [translationsQuery.data, form]);

  async function runTranslate(target: TranslationTarget | "all") {
    if (!detail) return;
    setTranslationBusy(target === "all" || target === "commentary" ? target : target.matterId);
    setTranslationError(null);
    try {
      const body = target === "all" ? {} : { targets: [target], force: true };
      const result = await researchCms.translateContent(detail.id, body);
      queryClient.setQueryData(["research-translations", detail.id], result);
      form.resetField("translations", { defaultValue: toTranslationsFormValues(result) });
      const count = result.translated.length;
      setToast({
        message: `${count} field${count === 1 ? "" : "s"} translated, ${result.skipped.length} kept`,
        type: "success",
      });
    } catch (err) {
      setTranslationError(err instanceof Error ? err.message : "Translation failed");
    } finally {
      setTranslationBusy(null);
    }
  }

  function reasonText(reason: unknown): string {
    return reason instanceof Error ? reason.message : "unknown error";
  }

  // Save asks before it writes. The plan is built from the card the researcher is looking at, so
  // the modal names exactly the sliders showing "—"; nothing is written until they confirm, and
  // cancelling leaves the form as it was, still unsaved and still guarded against navigation.
  function onSubmit(values: OrgFormValues) {
    if (isBusy) return;
    form.clearErrors("rankings");
    const unsavable = findUnsavableRankingEdits(values.rankings);
    if (unsavable.length > 0) {
      for (const tableId of unsavable) {
        form.setError(`rankings.${tableId}.tier`, {
          type: "manual",
          message: UNSAVABLE_RANKING_MESSAGE,
        });
      }
      setActiveTab("rankings");
      return;
    }
    const firmNames = findFirmTagsToConfirm(commentary?.postContent, values.postContent);
    if (firmNames.length > 0) {
      setSaveError(null);
      setPendingValues(values);
      setTaggedFirmNames(firmNames);
      return;
    }
    confirmUnscoredThenSave(values);
  }

  function confirmTaggedFirmsSave() {
    const values = pendingValues;
    setTaggedFirmNames(null);
    setPendingValues(null);
    if (values) confirmUnscoredThenSave(values);
  }

  function cancelTaggedFirmsSave() {
    setTaggedFirmNames(null);
    setPendingValues(null);
  }

  function confirmUnscoredThenSave(values: OrgFormValues) {
    const plan = planSarsWrite(values.sarScores, sarsSliders);
    if (plan.kind === "partial") {
      setSaveError(null);
      setPendingValues(values);
      setUnscoredLabels(plan.missingLabels);
      return;
    }
    void performSave(values);
  }

  function confirmPartialSave() {
    const values = pendingValues;
    setUnscoredLabels(null);
    setPendingValues(null);
    if (values) void performSave(values);
  }

  function cancelPartialSave() {
    setUnscoredLabels(null);
    setPendingValues(null);
  }

  async function performSave(values: OrgFormValues) {
    if (isBusy) return;
    setSaveError(null);

    setIsSaving(true);
    try {
      let target: EditorialContentDetail;
      let contentValues: OrgFormValues;
      let sarsPlan: SarsWritePlan;

      if (isManualEntry) {
        if (!canCreateManualEntry) {
          setSaveError(MANUAL_ENTRY_PERMISSION_MESSAGE);
          return;
        }
        if (hasCommentaryContent(values) && !hasSubmissionContent(values)) {
          if (!editionBpaId) {
            setSaveError(NO_EDITION_BPA_MESSAGE);
            return;
          }
          const saved = await withSaveTimeout(() =>
            researchCms.upsertEditorialCommentary(editionBpaId, {
              companyId: companyUuid,
              languageIso: commentaryLanguage(null, publication),
              ...toCommentaryRequest(values),
            }),
          );
          setSavedCommentary(saved.commentary);
          form.reset(values);
          setToast({ message: "Saved", type: "success" });
          return;
        }
        if (!directoryId) {
          setSaveError(
            "This edition is not linked to a directory, so a manual record cannot be created.",
          );
          return;
        }
        // Retrying after a failed update patches the row the first attempt created.
        // No id to score until this returns, so the SARs write can only follow it.
        target =
          createdContent ??
          (await createOrAdoptOrgContent({
            companyId: companyUuid,
            researchBpaId: bpaId,
            editionId,
            directoryId,
            firmName,
            rankingTableId,
            officeId: navState?.officeId,
            officeName: navState?.officeName,
          }));
        setCreatedContent(target);
        // A conflict may have adopted a pre-filled row; fields the form left empty must
        // carry that row's values into the PATCH, or the save wipes them.
        contentValues = mergeAdoptedValues(values, target);
        // Same for the row's own recorded scores: they — not just this save's form — decide
        // what gets written here.
        const recorded = (await researchCms.getSars(target.id)).sars;
        sarsPlan = planSarsWrite(mergeSarScores(values.sarScores, recorded), sarsSliders);
      } else {
        if (!detail) return;
        if (!canEdit) {
          setSaveError("You do not have permission to edit content.");
          return;
        }
        target = detail;
        contentValues = values;
        sarsPlan = planSarsWrite(values.sarScores, sarsSliders);
      }

      // One Save, five destinations: content PATCH, commentary upsert, SARs PUT, rankings PUT,
      // translations PUT. allSettled so one failure doesn't hide another's success.
      const wantsCommentary = commentary !== null || hasCommentaryContent(values);
      const sarsBody =
        sarsPlan.kind === "partial" || sarsPlan.kind === "complete" ? sarsPlan.body : null;

      const rankingSaves = Object.entries(contentValues.rankings ?? {})
        .map(([tableId, r]) => {
          const shouldExist = r.ranked || r.tier !== null;
          if (r.rankingId && !shouldExist) {
            return () => researchCms.deleteFirmRanking(r.rankingId!);
          }
          if (r.rankingId) {
            return () =>
              researchCms.updateFirmRanking(r.rankingId!, {
                rankingTier: r.tier,
                status: r.status,
                rankingComment: r.comment || null,
                displayOrder: r.displayOrder,
              });
          }
          if (shouldExist) {
            return () =>
              researchCms.createFirmRanking(tableId, {
                companyId: companyUuid,
                firmName,
                rankingTier: r.tier ?? undefined,
                status: r.status,
                rankingComment: r.comment || undefined,
                displayOrder: r.displayOrder,
              });
          }
          return null;
        })
        .filter(Boolean) as Array<() => Promise<unknown>>;

      const translationsBody =
        !isManualEntry && translationsEnabled
          ? translationsSaveBody(values.translations, form.formState.dirtyFields.translations ?? {})
          : null;

      const [contentResult, commentaryResult, sarsResult, rankingsResult, translationsResult] =
        await Promise.allSettled([
          withSaveTimeout(() =>
            researchCms.updateEditorialContent(target.id, {
              ...toUpdateRequest(contentValues, target),
              ...rankingTableStamp(rankingTableId, target),
            }),
          ),
          wantsCommentary && editionBpaId
            ? withSaveTimeout(() =>
                researchCms
                  .upsertEditorialCommentary(editionBpaId, {
                    companyId: companyUuid,
                    languageIso: commentaryLanguage(target, publication),
                    ...toCommentaryRequest(values),
                  })
                  .then(result => result.commentary),
              )
            : Promise.resolve(null),
          sarsBody
            ? withSaveTimeout(() => researchCms.putSars(target.id, sarsBody))
            : Promise.resolve(null),
          rankingSaves.length > 0
            ? Promise.all(rankingSaves.map(fn => fn()))
            : Promise.resolve(null),
          translationsBody
            ? withSaveTimeout(() => researchCms.saveTranslations(target.id, translationsBody))
            : Promise.resolve(null),
        ]);

      if (commentaryResult.status === "fulfilled" && commentaryResult.value) {
        setSavedCommentary(commentaryResult.value);
      }
      if (translationsResult.status === "fulfilled" && translationsResult.value) {
        queryClient.setQueryData(["research-translations", target.id], translationsResult.value);
        contentValues = {
          ...contentValues,
          translations: toTranslationsFormValues(translationsResult.value),
        };
      }

      const failures: string[] = [];
      if (contentResult.status === "rejected") {
        failures.push(`submission fields: ${saveErrorText(contentResult.reason)}`);
      }
      if (commentaryResult.status === "rejected") {
        failures.push(`editorial commentary: ${reasonText(commentaryResult.reason)}`);
      }
      if (wantsCommentary && !editionBpaId) {
        failures.push("editorial commentary: this practice area is not linked to the edition");
      }
      if (sarsResult.status === "rejected") {
        failures.push(`SARs scores: ${reasonText(sarsResult.reason)}`);
      }
      if (rankingsResult.status === "rejected") {
        failures.push(`rankings: ${reasonText(rankingsResult.reason)}`);
      }
      if (translationsResult.status === "rejected") {
        failures.push(`translations: ${reasonText(translationsResult.reason)}`);
      }
      if (failures.length > 0) {
        setSaveError(`Some changes failed to save — ${failures.join("; ")}`);
        return;
      }

      // Reset with the merged values, so the form shows what was actually saved.
      form.reset(contentValues);
      // Rankings are their own query too; refetched in the same batch, or the hydration effect
      // sees fresh content beside the cached tiers.
      await Promise.all([
        refetchContentAndScores(),
        rankingSaves.length > 0 ? refetchRankings() : null,
      ]);
      if (rankingSaves.length > 0) {
        void queryClient.invalidateQueries({
          queryKey: ["research-org-list", editionId, bpaId],
        });
        void queryClient.invalidateQueries({
          queryKey: ["research-ranking-table-entries"],
        });
        void queryClient.invalidateQueries({
          queryKey: ["research-org-all-content", editionId, bpaId, companyUuid],
        });
      }
      // English edits change the source column and stale flags the translations view carries.
      if (translationsEnabled) {
        void queryClient.invalidateQueries({ queryKey: ["research-translations", target.id] });
      }
      if (rankingTableId) draftsByTable.current.delete(rankingTableId);
      setToast({ message: "Saved", type: "success" });
    } catch (err) {
      setSaveError(saveErrorText(err));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleStatusChange(nextStatus: EditorialWriteStatus) {
    // isDirty: the refetch below re-hydrates the form, dropping unsaved edits without a prompt.
    if (!detail || !canEdit || isBusy || isDirty) return;
    setSaveError(null);
    setIsTransitioning(true);
    try {
      await withSaveTimeout(() =>
        researchCms.updateEditorialContent(detail.id, {
          reviewStatus: nextStatus,
        }),
      );
      await refetch();
      setToast({
        message: STATUS_CHANGE_MESSAGES[nextStatus],
        type: "success",
      });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to change status");
    } finally {
      setIsTransitioning(false);
    }
  }

  function handleDiscard() {
    if (window.confirm("Are you sure? Unsaved changes will be lost.")) {
      form.reset();
      setSaveError(null);
    }
  }

  if (!hasPermission("staff.research.read")) return <UnauthorizedView />;

  if (!hasNavState && apiLoading) {
    return (
      <CmsLayout
        breadcrumbs={[
          {
            label: "Loading...",
            path: ROUTES.research.cmsPublication(editionId),
          },
        ]}
      >
        <PageHeader title="Loading..." subtitle="" />
      </CmsLayout>
    );
  }

  if (error && !hasLoaded) {
    return (
      <CmsLayout
        breadcrumbs={[
          {
            label: editionName,
            path: ROUTES.research.cmsPublication(editionId),
          },
        ]}
      >
        <PageHeader title="Failed to load organisation" subtitle="" />
        <div className={styles.errorMessage} role="alert">
          {error.message}
        </div>
      </CmsLayout>
    );
  }

  const firmName = addressBookCompany?.data?.name?.trim() || companyName || detail?.firmName || "";
  const canSave = isManualEntry ? canCreateManualEntry : canEdit;
  const showFooter = !isDeleted && (canEdit || canCreate);
  const statusActionsBlocked = isDirty && canEdit && detail !== null && !isDeleted;
  const inlineError =
    saveError ??
    (error ? `Could not refresh this page: ${error.message}` : null) ??
    (sarsError ? `Scores could not be read: ${sarsError.message}` : null);

  function orgSourcePanelSource(): SourceDataSource {
    if (rankingTablesFailed || error) return { kind: "error" };
    if (data === undefined) return { kind: "pending" };
    if (!tableResolved) return { kind: "pending" };
    return {
      kind: "known",
      docParseResultId: isDeleted ? null : (detail?.docParseResultId ?? null),
    };
  }
  const sourcePanelSource = orgSourcePanelSource();

  return (
    <CmsLayout
      aside={
        hasFlag("research-cms-1-enabled") ? (
          <SourceDataPanel
            title={firmName}
            source={sourcePanelSource}
            templateType={effectiveTemplateType}
            firm={{ kind: "known", companyUuid }}
            editionId={editionId}
            researchBpaId={bpaId}
            names={{ editionName, rankingEntityName, bpaName }}
          />
        ) : undefined
      }
      breadcrumbs={[
        {
          label: editionName,
          path: ROUTES.research.cmsPublication(editionId),
        },
        // Links back to this country's practice areas, but only once the id is known — a direct
        // load before the BPA read lands has the name alone, and a link without it goes nowhere.
        ...(rankingEntityName
          ? [
              {
                label: rankingEntityName,
                ...(rankingEntityId
                  ? {
                      path: `${ROUTES.research.cmsPublication(editionId)}?rankingEntity=${rankingEntityId}`,
                    }
                  : {}),
              },
            ]
          : []),
        { label: bpaName, path: ROUTES.research.cmsBpa(editionId, bpaId) },
        { label: firmName },
      ]}
    >
      <OrgBanner
        firmName={firmName}
        companyUuid={companyUuid}
        hasSubmission={aggregateContent.some(row => row.docParseResultId != null)}
        fileUri={fileUrlData?.fullUri ?? null}
        reviewStatus={detail?.reviewStatus ?? aggregateContent[0]?.reviewStatus ?? null}
        onStatusChange={canEdit ? handleStatusChange : undefined}
        canSetStatus={canSetStatus}
        busy={isBusy}
        statusActionsDisabled={statusActionsBlocked}
        showTranslationStep={needsTranslation}
        editionStatus={editionStatus}
        officeId={
          detail?.officeId ?? aggregateContent.find(r => r.officeId)?.officeId ?? navState?.officeId
        }
        officeName={
          detail?.officeName ??
          aggregateContent.find(r => r.officeName)?.officeName ??
          navState?.officeName
        }
      />

      <FormProgressBar filled={filledFieldCount} total={totalFields} isDirty={isDirty} />

      <div className={styles.tabBar} role="tablist" aria-label="Organisation sections">
        {TABS.filter(tab => {
          if (tab.id === "editorial" && !hasFirmSubmission) return false;
          if (tab.id === "submission" && !hasFirmSubmission) return false;
          if (tab.id === "translations" && !translationsEnabled) return false;
          return true;
        }).map(tab => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ""}`}
            aria-selected={activeTab === tab.id}
            aria-controls={`panel-${tab.id}`}
            id={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className={styles.form}>
        <fieldset
          className={styles.formFields}
          disabled={isDeleted}
          aria-label={isDeleted ? "Editorial content (read-only)" : "Editorial content"}
        >
          {isHydrated ? (
            <div className={`${styles.tabContent} ${shared.fadeIn}`}>
              <div
                id="panel-submission"
                role="tabpanel"
                aria-labelledby="tab-submission"
                style={{
                  display: activeTab === "submission" ? "block" : "none",
                }}
              >
                {orgSubmissionTables.length > 1 && (
                  <div className={styles.subTabBar} role="tablist" aria-label="Ranking table tabs">
                    {orgSubmissionTables.map(table => (
                      <button
                        key={table.id}
                        type="button"
                        role="tab"
                        className={`${styles.subTab} ${activeTableId === table.id ? styles.subTabActive : ""}`}
                        aria-selected={activeTableId === table.id}
                        disabled={isBusy}
                        onClick={() => {
                          if (activeTableId) {
                            draftsByTable.current.set(activeTableId, form.getValues());
                          }
                          setSelectedTableId(table.id);
                        }}
                      >
                        {table.name}
                      </button>
                    ))}
                  </div>
                )}
                {detail && hasFlag("research-cms-1-enabled") && (
                  <AutoTranslatedNotice
                    detectedLanguage={detail.detectedLanguage}
                    languageIso={detail.languageIso}
                  />
                )}
                {isManualEntry && !manualTabsOpened.has(rankingTableId ?? "") ? (
                  <div className={styles.noSubmission}>
                    <p className={styles.noSubmissionText}>No submission for this ranking table.</p>
                    <button
                      type="button"
                      className={styles.noSubmissionButton}
                      onClick={() => {
                        setManualTabsOpened(prev => new Set([...prev, rankingTableId ?? ""]));
                      }}
                    >
                      Create manual entry
                    </button>
                  </div>
                ) : (
                  <TemplateSubmissionTab
                    control={form.control}
                    register={form.register}
                    watch={form.watch}
                    setValue={form.setValue}
                    errors={form.formState.errors}
                    fields={submissionFields}
                    sliders={sarsSliders}
                    templateType={effectiveTemplateType}
                  />
                )}
              </div>

              <div
                id="panel-editorial"
                role="tabpanel"
                aria-labelledby="tab-editorial"
                style={{
                  display: activeTab === "editorial" ? "block" : "none",
                }}
              >
                <EditorialTab
                  control={form.control}
                  register={form.register}
                  showClerksCommentary={showClerksCommentary}
                />
              </div>

              <div
                id="panel-translations"
                role="tabpanel"
                aria-labelledby="tab-translations"
                style={{ display: activeTab === "translations" ? "block" : "none" }}
              >
                {translationsEnabled && (
                  <TranslationsTab
                    view={translationsView}
                    loading={translationsQuery.isLoading}
                    errorMessage={translationError ?? translationsQuery.error?.message ?? null}
                    gateMessage={translationsGateOpen ? null : GATE_MESSAGE}
                    autoPopulated={
                      detail?.detectedLanguage != null &&
                      detail.detectedLanguage === publication?.outputLanguage &&
                      detail.detectedLanguage !== detail.languageIso
                    }
                    canEdit={canEdit && !isDeleted}
                    dirty={translationsDirty}
                    busyTarget={translationBusy}
                    control={form.control}
                    register={form.register}
                    onTranslateAll={() => runTranslate("all")}
                    onRegenerate={runTranslate}
                  />
                )}
              </div>

              <div
                id="panel-details"
                role="tabpanel"
                aria-labelledby="tab-details"
                style={{ display: activeTab === "details" ? "block" : "none" }}
              >
                {detail ? (
                  <DetailsTab
                    content={detail}
                    ranking={
                      rankingTableId
                        ? {
                            rankingTableId,
                            rankingTableName: selectedTableName,
                            rankingId: scopedRankingId,
                          }
                        : undefined
                    }
                    rankings={firmRankingsMetadata}
                  />
                ) : (
                  <CmsEmptyState message="Metadata appears once this firm has a saved record." />
                )}
              </div>

              <div
                id="panel-rankings"
                role="tabpanel"
                aria-labelledby="tab-rankings"
                style={{ display: activeTab === "rankings" ? "block" : "none" }}
              >
                <RankingsTab
                  tables={firmTables}
                  scopedTableId={undefined}
                  entityType="firm"
                  canEdit={canEdit}
                  canApprove={canApprove}
                  control={form.control}
                  previousTiers={previousTiers}
                  editionId={editionId}
                  bpaId={bpaId}
                />
              </div>
            </div>
          ) : (
            <CmsEmptyState message="Loading editorial content" loading />
          )}
        </fieldset>

        {inlineError && (
          <div className={styles.saveError} role="alert">
            {inlineError}
          </div>
        )}

        {showFooter && (
          <div className={shared.footer}>
            {isDirty && (
              <button type="button" className={shared.discardButton} onClick={handleDiscard}>
                Discard changes
              </button>
            )}
            <button type="submit" className={shared.saveButton} disabled={!canSave || isBusy}>
              {isSaving ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </form>

      {navGuard.blocked && (
        <UnsavedChangesModal
          onCancel={navGuard.cancel}
          onDiscard={() => {
            form.reset();
            navGuard.proceed();
          }}
        />
      )}

      {taggedFirmNames && (
        <MultipleFirmTagsModal
          firmNames={taggedFirmNames}
          onConfirm={confirmTaggedFirmsSave}
          onCancel={cancelTaggedFirmsSave}
        />
      )}

      {unscoredLabels && (
        <UnscoredQuestionsModal
          missingLabels={unscoredLabels}
          onConfirm={confirmPartialSave}
          onCancel={cancelPartialSave}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </CmsLayout>
  );
}
